import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { EChartsCoreOption } from 'echarts/core';
// The ESM build, not `lib/core`. `lib/` is CommonJS (`exports.default = …`), and under Vite's interop a
// default import of it resolves to the module namespace object rather than the component, which React
// rejects with "Element type is invalid … got: object". jsdom never renders this branch — canvas is
// unavailable there, so canRenderCanvas() is false — which is why only a real browser caught it.
import ReactEChartsCore from 'echarts-for-react/esm/core';

import { canRenderCanvas } from './canvasSupport';
import { useChartScheme } from './chartTheme';
import { echarts } from './echartsCore';

export interface ChartTableColumn {
  readonly header: string;
  readonly values: readonly string[];
}

export interface ChartProps {
  readonly ariaLabel: string;
  readonly columns: readonly ChartTableColumn[];
  readonly height?: number;
  readonly option: EChartsCoreOption;
  readonly rowHeader: string;
  readonly rows: readonly string[];
  readonly tableCaption: string;
  /**
   * Set when the caller already renders a *visible* table of the same data. The hidden table exists to be
   * the chart's text alternative; when a real one is on the page it is the better alternative, and adding
   * a second makes assistive technology read the same figures twice. Defaults to false, so the
   * alternative is present unless a caller takes explicit responsibility for it.
   */
  readonly tableProvidedByCaller?: boolean;
}

export function Chart({
  ariaLabel,
  columns,
  height = 260,
  option,
  rowHeader,
  rows,
  tableCaption,
  tableProvidedByCaller = false,
}: ChartProps) {
  const scheme = useChartScheme();

  return (
    <Box aria-label={ariaLabel} role="group">
      {canRenderCanvas() ? (
        <ReactEChartsCore
          echarts={echarts}
          notMerge
          option={option}
          style={{ height, width: '100%' }}
          // The scheme's resolved colours are baked into `option`, so a scheme change must rebuild the
          // instance rather than merge into the old one. This keys on the resolved scheme, not on
          // theme.palette.mode, which is frozen at 'light' under cssVariables.
          key={scheme}
        />
      ) : null}
      {tableProvidedByCaller ? null : (
        <Box sx={visuallyHidden}>
          <Table aria-label={tableCaption} size="small">
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">
                  {rowHeader}
                </TableCell>
                {columns.map((column) => (
                  <TableCell component="th" key={column.header} scope="col">
                    {column.header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={row}>
                  <TableCell component="th" scope="row">
                    {row}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.header}>{column.values[rowIndex] ?? ''}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

// Kept off-screen rather than display:none, so assistive technology can still reach it.
// The sizes must be strings: sx runs width/height through MUI's sizingTransform, which turns any
// unitless number <= 1 into a percentage (sizing.js:19), so a bare `1` would collapse to `100%` and
// give this box a viewport-sized layout footprint instead of a 1px one.
const visuallyHidden = {
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;
